import { Alert, Box, Button, Group, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import type { CaseCustomFieldWire } from '../../api/integrations';
import { useCaseCustomFields } from './use-case-custom-fields';
import { useSaveCaseCustomFields } from './use-save-case-custom-fields';
import { CustomFieldRow } from './custom-field-row';

type EditableField = Omit<CaseCustomFieldWire, 'display_order'>;

interface Props {
  orgId: string;
  productId: string;
  /** Hide all edit controls (support role). */
  readOnly: boolean;
}

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 63);
}

export function CustomFieldsPanel({ orgId, productId, readOnly }: Props) {
  const query = useCaseCustomFields(orgId, productId);
  const save = useSaveCaseCustomFields(orgId, productId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditableField[]>([]);

  // Reset draft whenever the server snapshot changes.
  useEffect(() => {
    if (query.data) {
      setDraft(
        query.data.fields.map((f) => ({
          field_key: f.field_key,
          label: f.label,
          description: f.description,
          required: f.required,
          max_length: f.max_length,
        })),
      );
    }
  }, [query.data]);

  if (query.isPending) return <Text c="dimmed">Loading…</Text>;
  if (query.isError || !query.data) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Could not load custom fields.
      </Alert>
    );
  }

  const hasFields = draft.length > 0;

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={5}>Custom fields</Title>
        {!readOnly && !editing && (
          <Button variant="subtle" size="xs" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {!readOnly && editing && (
          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              onClick={() => {
                setEditing(false);
                if (query.data) {
                  setDraft(
                    query.data.fields.map((f) => ({
                      field_key: f.field_key,
                      label: f.label,
                      description: f.description,
                      required: f.required,
                      max_length: f.max_length,
                    })),
                  );
                }
              }}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              loading={save.isPending}
              onClick={() =>
                save.mutate(draft, {
                  onSuccess: () => setEditing(false),
                })
              }
            >
              Save
            </Button>
          </Group>
        )}
      </Group>

      {save.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {save.error.message}
        </Alert>
      )}

      {!hasFields && !editing && (
        <Box>
          <Text c="dimmed" size="sm">
            This endpoint accepts only the standard fields. Add custom fields to capture extra data
            specific to this product.
          </Text>
          {!readOnly && (
            <Button leftSection={<IconPlus size={14} />} mt="xs" onClick={() => setEditing(true)}>
              Add field
            </Button>
          )}
        </Box>
      )}

      {hasFields && (
        <Stack gap="xs">
          {draft.map((f, i) => (
            <CustomFieldRow
              key={i}
              fieldKey={f.field_key}
              label={f.label}
              required={f.required}
              maxLength={f.max_length}
              editing={editing}
              onChange={(next) =>
                setDraft((d) =>
                  d.map((row, j) =>
                    j === i
                      ? {
                          field_key: next.fieldKey,
                          label: next.label,
                          description: row.description,
                          required: next.required,
                          max_length: next.maxLength,
                        }
                      : row,
                  ),
                )
              }
              onRemove={() => setDraft((d) => d.filter((_, j) => j !== i))}
            />
          ))}
        </Stack>
      )}

      {editing && (
        <Button
          variant="subtle"
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            setDraft((d) => [
              ...d,
              {
                field_key: '',
                label: '',
                description: null,
                required: false,
                max_length: 1000,
              },
            ])
          }
          disabled={draft.length >= 50}
        >
          Add field
        </Button>
      )}
    </Stack>
  );
}

// Exported for the integration page's slug-derivation helper if needed.
export { slugify };
